import React from "react";
import Modal from "../../../shared/ui/Modal";
import { toast } from "../../../shared/ui/toast";
import { formatNumber } from "../../../shared/lib/helpers";
import type { AuthContextType } from "../../auth/AuthContext";
import { fetchCurrentItemAmounts, retrieveItem, updateItem, updateItemAmount } from "../items.api";
import { buildItemQuickEditPayload, getItemLowerLimit, getItemTargetRankMax } from "../model/item-list-quick-edit";
import type { ItemQuickEditData, ItemQuickEditField } from "../model/item-list-quick-edit";

export type ItemListValueEditField = "price" | "discount" | ItemQuickEditField;

interface ItemListValueEditModalProps {
  auth: AuthContextType;
  itemId: string | null;
  field: ItemListValueEditField;
  onHide: () => void;
  onComplete: (itemId: string, field: ItemListValueEditField) => void;
}

export default function ItemListValueEditModal({ auth, itemId, field, onHide, onComplete }: ItemListValueEditModalProps) {
  const authRef = React.useRef(auth);
  const onCompleteRef = React.useRef(onComplete);
  const [item, setItem] = React.useState<ItemQuickEditData | null>(null);
  const [currentAmount, setCurrentAmount] = React.useState<number | null>(null);
  const [newValue, setNewValue] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isShopAmount = field === "price" || field === "discount";

  React.useEffect(() => { authRef.current = auth; }, [auth]);
  React.useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  React.useEffect(() => {
    if (itemId === null) return;
    let active = true;
    setItem(null);
    setCurrentAmount(null);
    setNewValue("");
    setErrorMessage("");
    if (field === "price" || field === "discount") {
      void fetchCurrentItemAmounts(authRef.current, itemId)
        .then((amounts) => {
          if (!active) return;
          setCurrentAmount(amounts[field]);
          setNewValue(String(amounts[field]));
          if (amounts.synced) onCompleteRef.current(itemId, field);
        })
        .catch((error: Error) => { if (active) setErrorMessage(error.message); });
    } else {
      void retrieveItem({
        auth: authRef.current,
        id: itemId,
        setErrorMessage: (message: string) => { if (active) setErrorMessage(message); },
        setResult: (data: ItemQuickEditData) => {
          if (!active) return;
          setItem(data);
          const value = field === "lower_limit" ? getItemLowerLimit(data).range.value_min : data[field];
          setNewValue(String(value ?? 0));
        },
      });
    }
    return () => { active = false; };
  }, [itemId, field]);

  const lowerLimit = item && field === "lower_limit" ? getItemLowerLimit(item) : null;
  const label = field === "price" ? "판매가" : field === "discount" ? "할인액"
    : field === "price_default" ? "복구기준가" : field === "target_rank" ? "기준순위" : lowerLimit?.label ?? "조정하한가";
  const unit = field === "target_rank" ? "위" : lowerLimit?.range.value_min_type === "percent" ? "%" : "원";
  const currentValue = isShopAmount ? currentAmount
    : item ? field === "lower_limit" ? lowerLimit?.range.value_min : item[field] : null;
  const maxRank = item ? getItemTargetRankMax(item) : 0;
  const isReady = isShopAmount ? currentAmount !== null : item !== null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (itemId === null || !isReady || isSubmitting) return;
    const value = Number(newValue);
    if (!newValue.trim() || !Number.isSafeInteger(value) || value < (field === "target_rank" ? 1 : 0)) {
      setErrorMessage(`변경할 ${label}을 올바르게 입력해 주세요.`);
      return;
    }
    if (field === "target_rank" && value > maxRank) {
      setErrorMessage(`기준순위는 ${maxRank}위까지 설정할 수 있습니다.`);
      return;
    }
    if (lowerLimit?.field === "adjust_discount_range") {
      const max = Number(lowerLimit.range.value_max ?? 0);
      if (value > 0 && max > 0 && value >= max) {
        setErrorMessage("하한은 상한보다 작아야 합니다.");
        return;
      }
    }
    if (value === Number(currentValue ?? 0)) {
      onHide();
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    if (field === "price" || field === "discount") {
      try {
        await updateItemAmount(authRef.current, itemId, field, value);
        toast.success(`${label} 변경이 완료되었습니다.`);
        onComplete(itemId, field);
        onHide();
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : `${label} 변경에 실패했습니다.`);
      } finally {
        setIsSubmitting(false);
      }
    } else if (item) {
      await updateItem({
        auth: authRef.current,
        id: itemId,
        payload: buildItemQuickEditPayload(item, field, value),
        setProcessMessage: () => {},
        setErrorMessage,
        setError: (_field: string, error: { message: string }) => setErrorMessage(error.message),
        setResult: () => {
          toast.success(`${label} 변경이 완료되었습니다.`);
          onComplete(itemId, field);
          onHide();
        },
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={itemId !== null} onHide={() => { if (!isSubmitting) onHide(); }} backdrop="static" size="sm">
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>{label} 수정</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}
          <div className="mb-3">
            <label className="form-label">현재 {label}</label>
            <div>{!isReady ? "불러오는 중..." : `${formatNumber(Number(currentValue ?? 0))}${unit}`}</div>
          </div>
          <div>
            <label htmlFor="item-list-edit-value" className="form-label">변경할 {label}</label>
            {field === "target_rank" ? (
              <select id="item-list-edit-value" className="form-select" value={newValue}
                onChange={(event) => setNewValue(event.target.value)} disabled={!isReady || isSubmitting || maxRank < 1} required>
                {maxRank < 1 && <option value="">선택 불가</option>}
                {Array.from({ length: maxRank }, (_, index) => (
                  <option key={index + 1} value={index + 1}>{index + 1}위</option>
                ))}
              </select>
            ) : (
              <div className="input-group">
                <input id="item-list-edit-value" type="number" min="0" step="1" className="form-control"
                  value={newValue} onChange={(event) => setNewValue(event.target.value)}
                  disabled={!isReady || isSubmitting} required />
                <span className="input-group-text">{unit}</span>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isSubmitting}>취소</button>
          <button type="submit" className="btn btn-primary" disabled={!isReady || isSubmitting || field === "target_rank" && maxRank < 1}>
            {isSubmitting ? "저장중..." : "저장"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
